/**
 * Reads the Overview's `OverviewInput` from the running game (W4 card C1): the player's ship from the sim columns,
 * the career (wallet, lease, cargo, contracts, story) from the M2 runtime, the place and route from the Reach, the
 * jurisdiction from the strategic client's system view, the last fleet alerts from the shared inbox, and the last
 * standing deltas (subscribed once, kept in a short list). Called by the shell's 4 Hz refresh only while the dashboard
 * is open; it allocates small UI objects then, never per frame.
 */
import type { GameContext } from '../../../../app/feature'
import type { Career } from '../../../../career/types'
import type { Vec3T } from '../../../../galaxy/layout/types'
import { stationIndexOf } from '../../../../data/m2'
import { jurisdictionChipText, jurisdictionTone } from '../../../../features/charterHud/hudModel'
import { focusMission, missionObjective } from '../../../../features/storyHud/model'
import { alertAt, alertOffers, fleetAlertInbox, fleetAlertTone, type FleetAlertInbox } from '../../../../fleet-link/presence'
import { knownSystemName } from '../../../../hud/reach/labels'
import { COLLECTIONS_CODE } from '../../../../space/contracts'
import { getM2 } from '../../../../space/world'
import type { FleetAlert, StandingDelta } from '../../../../strategic/types'
import { Energy, Hull, Life, Pos64, Shield, Ship, Weapons } from '../../../../world/components'
import { requirementText } from '../../../models/contracts'
import { factionName } from '../../../strategic/names'
import { loadDashboardConfig } from '../../config'
import { createReachStationDirectory, dockedActionModel, nearestKnownStation, setCourseTo, toNearestStation, type StationDirectory } from '../../data'
import { courseFor, type CourseTarget } from '../jobs/model'
import {
  OVERVIEW_ROWS,
  type AlertInput,
  type CargoInput,
  type CollectionsState,
  type ContractInput,
  type DockedInput,
  type LeaseInput,
  type OverviewInput,
  type ShipInput,
  type StandingInput,
  type WhereInput,
} from './model'

export interface OverviewSource {
  /** `alerts`: the alert objects behind `input.alerts` (same order). */
  read(): { input: OverviewInput; alerts: FleetAlert[] }
  /** Set course (C0): a known station (`setCourseTo`) or a system (`plotRoute`); a no-op outside the Reach. */
  course(target: CourseTarget): void
  dispose(): void
}

/** The newest `n` alerts with text in the inbox, newest first (the card's Jump to / Send wing run on these). */
export function recentAlertObjects(inbox: FleetAlertInbox, n: number): FleetAlert[] {
  const out: FleetAlert[] = []
  const oldest = Math.max(0, inbox.head - inbox.items.length)
  for (let i = inbox.head - 1; i >= oldest && out.length < n; i--) {
    const a = alertAt(inbox, i)
    if (a !== null && a.text !== '') out.push(a)
  }
  return out
}

/** Alerts as card rows. */
export function alertInputs(alerts: readonly FleetAlert[]): AlertInput[] {
  return alerts.map((a) => ({ text: a.text, tone: fleetAlertTone(a.kind), jump: alertOffers(a, 'jumpTo'), wing: alertOffers(a, 'sendWing') }))
}

/** Prepends a batch of standing deltas (newest first) to `list`, keeping `keep`. */
export function pushStanding(list: StandingInput[], deltas: readonly StandingDelta[], keep: number): void {
  for (const d of deltas) {
    if (d.delta === 0) continue
    list.unshift({ faction: factionName(d.factionId), delta: d.delta, reason: d.reason })
  }
  if (list.length > keep) list.length = keep
}

function shipOf(ctx: GameContext): ShipInput | null {
  const p = ctx.sim.playerEid
  if (p < 0) return null
  const def = ctx.sim.defs[Ship.defIndex[p]]
  if (def === undefined) return null
  const m2 = getM2(ctx)
  return {
    alive: Life.alive[p] === 1,
    hull: def.hull > 0 ? Hull.value[p] / def.hull : 0,
    shield: def.shield > 0 ? Shield.value[p] / def.shield : 0,
    energy: def.energy > 0 ? Energy.value[p] / def.energy : 0,
    torpedoes: Weapons.torpedoes[p],
    tool: m2?.space.player.mode ?? 0,
  }
}

function collectionsOf(code: number): CollectionsState {
  return code === COLLECTIONS_CODE.repo ? 'repo' : code === COLLECTIONS_CODE.notice ? 'notice' : 'none'
}

function leaseOf(career: Career): LeaseInput {
  const l = career.lease
  return {
    owned: l.owned,
    payment: l.payment,
    nextDueAt: l.nextDueAt,
    principal: l.principal,
    collections: l.collections,
    arrears: l.arrears + l.feesDue,
    debt: career.debt,
  }
}

function contractOf(ctx: GameContext, career: Career, dir: StationDirectory | null): ContractInput | null {
  const m2 = getM2(ctx)
  if (m2 === null) return null
  let pick: Career['contracts']['active'][number] | null = null
  for (const c of career.contracts.active) if (pick === null || c.deadlineAt < pick.deadlineAt) pick = c
  if (pick === null) return null
  const i = stationIndexOf(m2.data, pick.station)
  return {
    requirement: requirementText(m2.data, pick.type, pick.station, pick.params),
    station: i < 0 ? pick.station : m2.data.stations.stations[i].name,
    deadlineAt: pick.deadlineAt,
    reward: pick.reward,
    ready: pick.status === 'ready',
    course: dir === null ? null : courseFor(pick, dir),
  }
}

function storyOf(career: Career): string | null {
  const m = focusMission(career)
  return m === null ? null : missionObjective(m)
}

/** The Pay line's DockedAction: the reason and the nearest known Lease Office (C0). */
function payAtOf(dir: StationDirectory | null): DockedInput | null {
  if (dir === null) return null
  const text = loadDashboardConfig().dockedAction
  const m = dockedActionModel(text, dir, 'lease-office')
  return { reason: m.reason, nearest: toNearestStation(text, m.hit), stationId: m.hit?.station.id ?? null }
}

function whereOf(ctx: GameContext, dir: StationDirectory | null): WhereInput | null {
  const reach = ctx.reach
  const layout = reach?.session.layout ?? null
  if (reach === null || layout === null) return null
  const p = ctx.sim.playerEid
  const px = p >= 0 ? Pos64.x[p] : 0
  const py = p >= 0 ? Pos64.y[p] : 0
  const pz = p >= 0 ? Pos64.z[p] : 0
  const dist = (v: readonly [number, number, number] | readonly number[]): number => Math.hypot(v[0] - px, v[1] - py, v[2] - pz)
  // The nearest station in this system; with none here, the nearest known one beyond it (C0).
  let station: string | null = null
  let stationJumps = 0
  let stationM = Infinity
  for (const s of layout.stations) {
    const d = dist(s.position)
    if (d < stationM) {
      stationM = d
      station = s.name
    }
  }
  if (station === null && dir !== null) {
    const hit = nearestKnownStation(dir)
    if (hit !== null) {
      station = hit.station.name
      stationJumps = hit.jumps
      stationM = hit.distanceM ?? 0
    }
  }
  const client = ctx.services.maybe('strategic')
  const view = client?.systemView ?? null
  const claim = view !== null && view.systemId === layout.systemId ? view.claim : null
  const relations = client?.relations ?? null
  const route = reach.state.route
  let routeIn: WhereInput['route'] = null
  if (route !== null && route.length > 0) {
    const here = route.indexOf(layout.systemId)
    const relay = layout.relays.find((r) => r.index === reach.nav.routeNextRelayIndex)
    routeIn = {
      dest: knownSystemName(reach.galaxy, reach.state, route[route.length - 1]),
      jumps: here < 0 ? route.length - 1 : route.length - 1 - here,
      next: relay === undefined ? null : knownSystemName(reach.galaxy, reach.state, relay.otherSystemId),
      nextM: relay === undefined ? 0 : dist(relay.position),
    }
  }
  return {
    system: layout.name,
    station,
    stationM: station === null ? 0 : stationM,
    stationJumps,
    law: client === undefined ? null : jurisdictionChipText(claim, relations),
    lawTone: jurisdictionTone(claim, relations),
    route: routeIn,
  }
}

/** The source over the live game; subscribes to the standing deltas once (until `dispose`). */
export function createOverviewSource(ctx: GameContext, inbox: FleetAlertInbox = fleetAlertInbox): OverviewSource {
  const standing: StandingInput[] = []
  const client = ctx.services.maybe('strategic')
  const off = client?.onStandingDeltas((d) => pushStanding(standing, d, OVERVIEW_ROWS)) ?? null
  // C0's known-station directory, one per Reach runtime (it caches the station list until knowledge grows).
  const pos: [number, number, number] = [0, 0, 0]
  let dirFor: { reach: object; dir: StationDirectory } | null = null
  const hereSystemId = (): string | null => ctx.reach?.session.layout?.systemId ?? null
  const directory = (): StationDirectory | null => {
    const reach = ctx.reach
    if (reach === null) return null
    if (dirFor?.reach !== reach) {
      const dir = createReachStationDirectory({
        galaxy: reach.galaxy,
        state: reach.state,
        layoutOf: (id) => reach.layoutOf(id),
        hereSystemId,
        herePosition: (): Vec3T | null => {
          const p = ctx.sim.playerEid
          if (p < 0 || hereSystemId() === null) return null
          pos[0] = Pos64.x[p]
          pos[1] = Pos64.y[p]
          pos[2] = Pos64.z[p]
          return pos
        },
      })
      dirFor = { reach, dir }
    }
    return dirFor.dir
  }
  return {
    read(): { input: OverviewInput; alerts: FleetAlert[] } {
      const m2 = getM2(ctx)
      const career = m2?.career() ?? null
      const dir = directory()
      const mirror = m2?.space.mirror ?? null
      const cargo: CargoInput | null = mirror === null ? null : { usedT: mirror.cargoUsedT, capT: mirror.cargoCapacityT }
      const lease = career === null ? null : leaseOf(career)
      // The live collections state (the mirror follows the lease between commands).
      const liveLease = lease !== null && mirror !== null ? { ...lease, collections: collectionsOf(mirror.collections) } : lease
      const alerts = recentAlertObjects(inbox, OVERVIEW_ROWS)
      const input: OverviewInput = {
        now: mirror?.gameTime ?? career?.gameTime ?? 0,
        ship: shipOf(ctx),
        cargo,
        wallet: career?.wallet ?? null,
        lease: liveLease,
        payAt: lease === null || lease.owned ? null : payAtOf(dir),
        contract: career === null ? null : contractOf(ctx, career, dir),
        contracts: career?.contracts.active.length ?? 0,
        story: career === null ? null : storyOf(career),
        alerts: alertInputs(alerts),
        where: whereOf(ctx, dir),
        standing: standing.slice(),
      }
      return { input, alerts }
    },
    course(target: CourseTarget): void {
      const reach = ctx.reach
      const dir = directory()
      if (reach === null || dir === null) return
      if (target.kind === 'station') setCourseTo(reach, dir, target.id)
      else reach.command({ kind: 'plotRoute', toSystemId: target.id, avoidHostile: false })
    },
    dispose(): void {
      off?.()
    },
  }
}

